import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AttributeDefinition, CatalogService } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-catalog-attribute-definitions-panel',
  imports: [
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    DialogModule
  ],
  template: `
    <ain-info-hint
      [titleKey]="'catalog.products.hub.attributes.hint.title'"
      [descriptionKey]="'catalog.products.hub.attributes.hint.description'"
      [bulletKeys]="[
        'catalog.products.hub.attributes.hint.bullets.perOffering',
        'catalog.products.hub.attributes.hint.bullets.capability'
      ]" />

    @if (loading()) {
      <ain-state type="loading" [title]="t('catalog.loading')" />
    } @else {
      <section class="toolbar">
        <button pButton type="button" [label]="t('catalog.products.hub.attributes.create')" (click)="openCreate()"></button>
        <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
      </section>

      <p-table [value]="rows()" [paginator]="rows().length > 25" [rows]="25">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('catalog.offerings.attributes.key') }}</th>
            <th>{{ t('columns.name') }}</th>
            <th>{{ t('columns.type') }}</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td><code>{{ row.key }}</code></td>
            <td>{{ row.displayName }}</td>
            <td>{{ row.type }}</td>
          </tr>
        </ng-template>
      </p-table>

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
      @if (message()) {
        <p class="message">{{ message() }}</p>
      }
    }

    <p-dialog [(visible)]="dialogVisible" [header]="t('catalog.products.hub.attributes.create')" [modal]="true" [style]="{ width: '480px' }">
      <div class="dialog-grid">
        <label>
          <span>{{ t('catalog.offerings.attributes.key') }}</span>
          <input pInputText [(ngModel)]="form.key" />
        </label>
        <label>
          <span>{{ t('columns.name') }}</span>
          <input pInputText [(ngModel)]="form.displayName" />
        </label>
        <label>
          <span>{{ t('columns.type') }}</span>
          <p-select [(ngModel)]="form.type" [options]="typeOptions()" optionLabel="label" optionValue="value" appendTo="body" />
        </label>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; margin: 12px 0; flex-wrap: wrap; }
    .dialog-grid { display: grid; gap: 10px; }
    .dialog-grid label { display: grid; gap: 6px; }
    .dialog-grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .error { color: #b91c1c; margin-top: 10px; }
    .message { color: #166534; margin-top: 10px; }
  `]
})
export class CatalogAttributeDefinitionsPanelComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly rows = signal<AttributeDefinition[]>([]);
  dialogVisible = false;

  form = { key: '', displayName: '', type: 'Text' };

  ngOnInit(): void {
    this.load();
  }

  typeOptions(): Array<{ label: string; value: string }> {
    return [
      { label: 'Boolean', value: 'Boolean' },
      { label: 'Text', value: 'Text' },
      { label: 'Number', value: 'Number' },
      { label: 'Date', value: 'Date' }
    ];
  }

  openCreate(): void {
    this.form = { key: '', displayName: '', type: 'Text' };
    this.dialogVisible = true;
  }

  save(): void {
    const key = this.form.key.trim();
    const displayName = this.form.displayName.trim();
    if (!key || !displayName) {
      return;
    }
    this.saving.set(true);
    this.error.set('');
    this.service.createAttributeDefinition({
      key,
      displayName,
      type: this.form.type,
      isFilterable: key.startsWith('capability_'),
      isSearchable: false
    }).subscribe({
      next: () => {
        this.saving.set(false);
        this.dialogVisible = false;
        this.message.set(this.t('catalog.offerings.attributes.definitionCreated'));
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.offerings.attributes.definitionCreateFailed'));
      }
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.service.listAttributeDefinitions().subscribe({
      next: rows => {
        this.rows.set(rows.sort((a, b) => a.key.localeCompare(b.key)));
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.error.set(this.t('catalog.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
